import React, { useState } from 'react';
import ModalShell from './ModalShell';

const VideoEmbed = ({
  type,
  videoId,
  thumbnailUrl,
  title,
  inModal = false,
  onClose
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  // Generate embed URL based on type
  const getEmbedUrl = () => {
    if (type === 'youtube') {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    } else if (type === 'vimeo') {
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    return null;
  };

  // Generate thumbnail URL if not provided
  const getThumbnailUrl = () => {
    if (thumbnailUrl) return thumbnailUrl;
    if (type === 'youtube') {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
    return null;
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  if (inModal) {
    return (
      <ModalShell
        isOpen={true}
        onClose={onClose}
        title={title || 'Video'}
        maxWidth="1200px"
        initialHeight="auto"
        bodyStyle={{ background: '#000' }}
        headerStyle={{ background: '#1a1a1a', borderBottom: '1px solid #333' }}
        titleStyle={{ color: '#fff' }}
      >
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' }}>
          <iframe
            src={getEmbedUrl()}
            title={title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        </div>
      </ModalShell>
    );
  }

  const thumbnail = getThumbnailUrl();

  return (
    <div className="video-embed">
      {!isPlaying ? (
        <div className="video-thumbnail" onClick={handlePlay}>
          {thumbnail ? (
            <img src={thumbnail} alt={title || 'Video thumbnail'} />
          ) : (
            <div className="video-placeholder">
              <i className="bi bi-play-circle"></i>
            </div>
          )}
          <div className="video-play-overlay">
            <div className="video-play-button">
              <i className="bi bi-play-fill"></i>
            </div>
          </div>
          <div className="video-badge">
            {type === 'youtube' ? (
              <i className="bi bi-youtube"></i>
            ) : (
              <i className="bi bi-vimeo"></i>
            )}
          </div>
        </div>
      ) : (
        <div className="video-embed-wrapper">
          <iframe
            src={getEmbedUrl()}
            title={title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      <style>{`
        .video-embed {
          width: 100%;
          border-radius: 8px;
          overflow: hidden;
        }

        .video-thumbnail {
          position: relative;
          cursor: pointer;
          aspect-ratio: 16/9;
          background: #1a1a1a;
        }

        .video-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        .video-thumbnail:hover img {
          transform: scale(1.05);
        }

        .video-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #072040 0%, #004080 100%);
        }

        .video-placeholder i {
          font-size: 4rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .video-play-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.3);
          transition: background 0.3s;
        }

        .video-thumbnail:hover .video-play-overlay {
          background: rgba(0, 0, 0, 0.5);
        }

        .video-play-button {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(0, 51, 102, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s, background 0.3s;
        }

        .video-thumbnail:hover .video-play-button {
          transform: scale(1.1);
          background: #072040;
        }

        .video-play-button i {
          font-size: 2rem;
          color: white;
          margin-left: 4px;
        }

        .video-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.7);
          border-radius: 4px;
          padding: 4px 8px;
        }

        .video-badge i {
          font-size: 1.25rem;
          color: white;
        }

        .video-badge .bi-youtube {
          color: #ff0000;
        }

        .video-badge .bi-vimeo {
          color: #1ab7ea;
        }

        .video-embed-wrapper {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
        }

        .video-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
      `}</style>
    </div>
  );
};

// Helper function to parse video URL and extract type/id
export const parseVideoUrl = (url) => {
  if (!url) return null;

  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return { type: 'youtube', videoId: match[1] };
    }
  }

  // Also check query string for YouTube
  if (url.includes('youtube.com')) {
    const urlObj = new URL(url);
    const videoId = urlObj.searchParams.get('v');
    if (videoId) {
      return { type: 'youtube', videoId };
    }
  }

  // Vimeo pattern
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return { type: 'vimeo', videoId: vimeoMatch[1] };
  }

  return null;
};

export default VideoEmbed;
